import React, { useEffect, useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';

export const AutoUpdateListener: React.FC = () => {
    const [initialVersion, setInitialVersion] = useState<number | null>(null);
    const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
    const [countdown, setCountdown] = useState(3);

    useEffect(() => {
        let isMounted = true;

        const checkVersion = async () => {
            try {
                const res = await fetch(`/version.json?t=${Date.now()}`, {
                    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
                });
                if (!res.ok) return;
                const data = await res.json();
                const fetchedVersion = data.version;

                if (!fetchedVersion) return;

                if (initialVersion === null) {
                    setInitialVersion(fetchedVersion);
                } else if (fetchedVersion !== initialVersion) {
                    if (isMounted) {
                        setIsNewVersionAvailable(true);
                    }
                }
            } catch (err) {
                // Silently ignore network check errors
            }
        };

        // Initial check
        checkVersion();

        // Check every 60 seconds
        const intervalId = setInterval(checkVersion, 60000);

        // Check on tab focus / visibility change
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [initialVersion]);

    useEffect(() => {
        if (!isNewVersionAvailable) return;

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    window.location.reload();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isNewVersionAvailable]);

    if (!isNewVersionAvailable) return null;

    return (
        <div className="fixed bottom-6 right-6 z-[9999] bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white p-4 rounded-2xl border border-indigo-500/40 shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
            <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-400">
                <Sparkles size={20} className="animate-spin" />
            </div>
            <div>
                <p className="text-xs font-black text-white flex items-center gap-1.5">
                    <span>사이트가 새 버전으로 업데이트되었습니다!</span>
                </p>
                <p className="text-[11px] text-slate-300 font-medium">
                    {countdown}초 후 최신 화면으로 자동 전환됩니다.
                </p>
            </div>
            <button
                onClick={() => window.location.reload()}
                className="ml-2 px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition-all flex items-center gap-1 shrink-0 shadow-md active:scale-95"
            >
                <RefreshCw size={13} />
                지금 새로고침
            </button>
        </div>
    );
};
